import { useState, useEffect, useCallback, useRef } from 'react'
import { Link, useSearchParams } from 'react-router'
import { Text } from '@carouselgg/nexus/text'
import { Spinner } from '@carouselgg/nexus/spinner'
import { Callout } from '@carouselgg/nexus/callout'
import { GradientText } from '@carouselgg/nexus/gradient-text'
import { NewsMasthead } from '@carouselgg/nexus/news-masthead'
import { NewsFront } from '@carouselgg/nexus/news-front'
import { MostRead } from '@carouselgg/nexus/most-read'
import type { MostReadStory } from '@carouselgg/nexus/most-read'
import { DiscordLogo } from '@carouselgg/nexus/icon'
import { listPlatformArticles, listPlatformArticleTopics, listMostReadPlatformArticles, toStory } from '../api/content'
import { discoverSeasons } from '../api/compete'
import type { DiscoverSeason } from '../api/compete'
import { seasonHref } from './compete/labels'
import type { PlatformArticle, TopicCount } from '../api/content'
import { DISCORD_INVITE } from '../constants/links'
import styles from './NewsPage.module.css'

const PAGE_SIZE = 12

export function NewsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const topic = searchParams.get('topic')
  const query = searchParams.get('q')

  const [topics, setTopics] = useState<TopicCount[]>([])
  const [mostRead, setMostRead] = useState<MostReadStory[]>([])
  const [openSeasons, setOpenSeasons] = useState<DiscoverSeason[]>([])
  const [articles, setArticles] = useState<PlatformArticle[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    listPlatformArticleTopics().then(setTopics).catch(() => setTopics([]))
    listMostReadPlatformArticles().then((list) => setMostRead(list.map(toStory))).catch(() => setMostRead([]))
    discoverSeasons(undefined, 3).then((d) => setOpenSeasons(d.seasons)).catch(() => setOpenSeasons([]))
  }, [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    listPlatformArticles(PAGE_SIZE, 0, topic, query)
      .then((data) => {
        if (cancelled) return
        setArticles(data.articles)
        setHasMore(data.has_more)
      })
      .catch((e: Error) => { if (!cancelled) setError(e.message) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [topic, query])

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return
    setLoadingMore(true)
    try {
      const data = await listPlatformArticles(PAGE_SIZE, articles.length, topic, query)
      setArticles((prev) => [...prev, ...data.articles])
      setHasMore(data.has_more)
    } catch (e) {
      setError((e as Error).message)
      setHasMore(false)
    } finally {
      setLoadingMore(false)
    }
  }, [loadingMore, hasMore, articles.length, topic, query])

  useEffect(() => {
    if (!hasMore) return
    const el = sentinelRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMore() },
      { rootMargin: '400px' },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [hasMore, loadMore])

  function changeTopic(next: string | null) {
    setSearchParams(next ? { topic: next } : {}, { replace: true })
  }

  function search(next: string | null) {
    setSearchParams(next ? { q: next } : {}, { replace: true })
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <NewsMasthead
          eyebrow="Carousel · Newsroom"
          title={<>Carousel <GradientText>News</GradientText></>}
          topics={topics.map((t) => ({ value: t.tag, count: t.count }))}
          activeTopic={topic}
          onTopicChange={changeTopic}
          search={{ query, onSearch: search, placeholder: 'Search Carousel News' }}
          className={styles.masthead}
        />

        {loading ? (
          <div className={styles.loading}><Spinner size="md" /></div>
        ) : error && articles.length === 0 ? (
          <Callout variant="error">{error}</Callout>
        ) : articles.length === 0 ? (
          <Text as="p" muted className={styles.empty}>
            {query ? `No articles match “${query}”.` : topic ? `No articles about ${topic} yet.` : 'No articles published yet. Check back soon!'}
          </Text>
        ) : (
          <NewsFront
            stories={articles.map(toStory)}
            layout={query ? 'river' : 'front'}
            latestLabel={query ? `Results for “${query}”` : 'Latest'}
            rail={<NewsRail mostRead={mostRead} openSeasons={openSeasons} />}
          >
            {loadingMore && (
              <div className={styles.loadingMore}>
                <Spinner size="sm" />
                <Text size="sm" muted>Loading more...</Text>
              </div>
            )}
            {error && <Callout variant="error">{error}</Callout>}
            {!hasMore && articles.length > PAGE_SIZE && (
              <Text size="sm" muted className={styles.endOfFeed}>You're all caught up</Text>
            )}
            {hasMore && <div ref={sentinelRef} className={styles.sentinel} />}
          </NewsFront>
        )}
      </div>
    </div>
  )
}

function NewsRail({ mostRead, openSeasons }: { mostRead: MostReadStory[]; openSeasons: DiscoverSeason[] }) {
  return (
    <>
      <MostRead stories={mostRead} />
      <section className={styles.railCard} aria-labelledby="news-find-league">
        <h2 id="news-find-league" className={styles.railLabel}>Find your league</h2>
        <p className={styles.railTitle}>Leagues and tournaments open for registration</p>
        {openSeasons.length > 0 ? (
          <ul className={styles.seasonList}>
            {openSeasons.map((s) => (
              <li key={s.id}>
                <Link to={seasonHref(s)} className={styles.seasonRow}>
                  <span className={styles.seasonName}>{s.name}</span>
                  <span className={styles.seasonMeta}>{s.game_name} · {s.org_name}</span>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <Text size="sm" muted>Pick a game and a rank bracket; every roster is checked for smurfs.</Text>
        )}
        <Link to="/compete" className={styles.railButton}>Browse competitions</Link>
      </section>
      <section className={styles.railCard} aria-labelledby="news-discord">
        <h2 id="news-discord" className={styles.railTitle}>Talk to the team building Carousel</h2>
        <Text size="sm" muted>Feature requests, bug reports and early looks.</Text>
        <a href={DISCORD_INVITE} target="_blank" rel="noopener noreferrer" className={styles.railSecondary}>
          <DiscordLogo size={16} /> Join the Discord
        </a>
      </section>
    </>
  )
}
